import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button, Card, Progress, Space, Tag, Typography, message } from "antd";
import { CheckCircleOutlined, CloudUploadOutlined, GlobalOutlined, LoadingOutlined, SkinOutlined } from "@ant-design/icons";
import { useAuth } from "contexts/AuthContext";
import paths from "routes/paths";
import { olreadiaService } from "services/olreadiaService";
import "./OlreadiaFlowPages.css";

const { Paragraph, Text, Title } = Typography;

const STEPS = [
  ["analyze", "Analyzing your business", <GlobalOutlined />],
  ["content", "Generating content", <LoadingOutlined />],
  ["design", "Applying your template", <SkinOutlined />],
  ["deploy", "Deploying your website", <CloudUploadOutlined />],
];

const doneStatuses = new Set(["READY", "COMPLETED", "DEPLOYED", "PUBLISHED", "ACTIVE", "SUCCESS"]);
const failedStatuses = new Set(["FAILED", "ERROR", "CANCELLED", "CANCELED"]);

export default function WebsiteGenerating() {
  const navigate = useNavigate();
  const { websiteId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const actorId = user?.id || user?.userId || "";
  const fallbackPreviewUrl = searchParams.get("previewUrl") || "";
  const fallbackSlug = searchParams.get("slug") || "";
  const [status, setStatus] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [error, setError] = useState("");

  const previewUrl = status?.previewUrl || fallbackPreviewUrl || (fallbackSlug ? olreadiaService.websites.previewUrl(fallbackSlug) : "");
  const normalizedStatus = String(status?.status || "").toUpperCase();
  const percent = useMemo(() => {
    if (doneStatuses.has(normalizedStatus)) return 100;
    if (failedStatuses.has(normalizedStatus)) return Math.max(25, stepIndex * 25);
    return Math.min(95, (stepIndex + 1) * 24);
  }, [normalizedStatus, stepIndex]);

  useEffect(() => {
    const timer = setInterval(() => {
      setStepIndex((current) => Math.min(STEPS.length - 1, current + 1));
    }, 1500);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!websiteId || !actorId) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const next = await olreadiaService.websites.status(websiteId, actorId);
        if (cancelled) return;
        setStatus(next);
        const nextStatus = String(next?.status || "").toUpperCase();
        if (doneStatuses.has(nextStatus)) {
          navigate(`${paths.websiteSuccess(websiteId)}?previewUrl=${encodeURIComponent(next.previewUrl || previewUrl || "")}`, { replace: true });
        }
        if (failedStatuses.has(nextStatus)) {
          setError("Website generation failed. Please update the info or try a different template.");
        }
      } catch (nextError) {
        if (!cancelled) setError(nextError?.message || "Could not check website generation status.");
      }
    };
    poll();
    const timer = setInterval(poll, 3500);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [actorId, navigate, previewUrl, websiteId]);

  return (
    <div className="olreadia-flow-page">
      <main className="olreadia-flow-main">
        <section className="olreadia-flow-hero">
          <span className="olreadia-flow-hero__icon"><CloudUploadOutlined /></span>
          <div>
            <Text className="olreadia-flow-eyebrow">Website generation</Text>
            <Title level={2}>Building your website</Title>
            <Paragraph>Olreadia is preparing content, applying your template, and deploying a preview.</Paragraph>
          </div>
          <Tag className={`olreadia-flow-status is-${error ? "bad" : "active"}`}>{error ? "Needs attention" : "In progress"}</Tag>
        </section>

        <Card className="olreadia-flow-card">
          <Progress percent={percent} strokeColor="#27ae30" />
          <Space direction="vertical" size={8} style={{ width: "100%", marginTop: 12 }}>
            {STEPS.map(([id, label, icon], index) => {
              const done = index < stepIndex || percent === 100;
              const active = index === stepIndex && percent < 100;
              return (
                <div key={id} className={`olreadia-flow-generation-step${done ? " is-done" : ""}${active ? " is-active" : ""}`}>
                  <span>{done ? <CheckCircleOutlined /> : icon}</span>
                  <strong>{label}</strong>
                  <Text>{done ? "Done" : active ? "Working" : "Pending"}</Text>
                </div>
              );
            })}
          </Space>
          {error ? (
            <div className="olreadia-flow-actions" style={{ marginTop: 12 }}>
              <Button onClick={() => navigate(paths.website)}>Back to Website</Button>
              {previewUrl ? <Button type="primary" href={previewUrl} target="_blank">Open Preview</Button> : null}
            </div>
          ) : null}
        </Card>
      </main>
    </div>
  );
}
